(function () {
  // Where a mouse has room for the card it trails the cursor and flips near a viewport
  // edge. On a coarse pointer or a narrow viewport there is no hover, so the first tap
  // reveals the card over the tile and the second one follows the product link.
  const SECTION_CLASS = "featured-collection-section";
  const ROOT_SELECTOR = ".featured-collection";
  const GRID_SELECTOR = ".featured-collection__grid";
  const TILE_SELECTOR = ".js-featured-product-card";
  const CURSOR_CARD_SELECTOR = ".featured-product-card__cursor-card";
  const ACTIVE_CLASS = "is-cursor-active";
  const CURSOR_MIN_WIDTH = 750; // $md in src/scss/base/variables/grid.scss
  const CURSOR_MODE = `(hover: hover) and (pointer: fine) and (min-width: ${CURSOR_MIN_WIDTH}px)`;
  const OFFSET = 24;
  // how far the tap card rides up over the bottom edge of its tile
  const TAP_OVERLAP = 32;

  class FeaturedCollectionCursor {
    constructor(root) {
      this.root = root;
      this.tiles = [];
      this.raf = null;
      this.pending = null;
      this.eventController = new AbortController();
      this.modeController = null;
      this.isCursorMode = false;

      const tiles = Array.from(root.querySelectorAll(TILE_SELECTOR)).filter(
        (tile) => tile.querySelector(CURSOR_CARD_SELECTOR)
      );

      if (!tiles.length) return;

      this.tiles = tiles;

      // the theme editor resizes the preview frame without reloading the section, so the
      // mode follows the query instead of being decided once at init
      if (typeof window.matchMedia === "function") {
        this.modeQuery = window.matchMedia(CURSOR_MODE);
        this.modeQuery.addEventListener("change", () => this._applyMode(), {
          signal: this.eventController.signal,
        });
      }

      this._applyMode();
    }

    destroy() {
      this._unbindMode();
      this.eventController.abort();
      this.tiles = [];
    }

    _applyMode() {
      this._unbindMode();

      this.isCursorMode = this.modeQuery ? this.modeQuery.matches : true;
      this.modeController = new AbortController();

      if (this.isCursorMode) this._bindPointer();
      else this._bindTap();
    }

    _unbindMode() {
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = null;
      this.pending = null;

      if (this.modeController) this.modeController.abort();
      this.modeController = null;

      this.tiles.forEach((tile) => {
        tile.classList.remove(ACTIVE_CLASS);

        const card = tile.querySelector(CURSOR_CARD_SELECTOR);
        if (!card) return;

        // only the tap mode reaches into the markup, so leaving it restores the authored
        // state and drops the coordinates the other mode reads in a different space
        card.setAttribute("inert", "");
        card
          .querySelectorAll("a[href]")
          .forEach((link) => link.removeAttribute("tabindex"));
        card.style.removeProperty("--cursor-card-x");
        card.style.removeProperty("--cursor-card-y");
      });
    }

    _bindPointer() {
      const signal = this.modeController.signal;

      this.tiles.forEach((tile) => {
        tile.addEventListener("pointerenter", (e) => this._onEnter(tile, e), {
          signal,
        });
        tile.addEventListener("pointermove", (e) => this._onMove(tile, e), {
          signal,
        });
        tile.addEventListener("pointerleave", () => this._onLeave(tile), {
          signal,
        });
      });
    }

    _bindTap() {
      const signal = this.modeController.signal;

      this.tiles.forEach((tile) => {
        const card = tile.querySelector(CURSOR_CARD_SELECTOR);
        // the card becomes tappable, but the tile's own link stays the single
        // link assistive tech sees for the product
        card.removeAttribute("inert");
        card
          .querySelectorAll("a[href]")
          .forEach((link) => link.setAttribute("tabindex", "-1"));

        tile.addEventListener("click", (e) => this._onTap(tile, e), { signal });
      });

      document.addEventListener(
        "click",
        (event) => {
          if (event.target.closest && event.target.closest(TILE_SELECTOR)) {
            return;
          }
          this._closeAll();
        },
        { signal }
      );
    }

    _isMouse(event) {
      return !event.pointerType || event.pointerType === "mouse";
    }

    _onEnter(tile, event) {
      if (!this._isMouse(event)) return;
      this._apply(tile, event.clientX, event.clientY);
      tile.classList.add(ACTIVE_CLASS);
    }

    _onMove(tile, event) {
      if (!this._isMouse(event)) return;
      this.pending = { tile: tile, x: event.clientX, y: event.clientY };
      if (this.raf) return;

      this.raf = requestAnimationFrame(() => {
        this.raf = null;
        const next = this.pending;
        this.pending = null;
        if (next) this._apply(next.tile, next.x, next.y);
      });
    }

    _onLeave(tile) {
      tile.classList.remove(ACTIVE_CLASS);
    }

    _onTap(tile, event) {
      // the revealed card carries its own link to the product
      if (event.target.closest(CURSOR_CARD_SELECTOR)) return;
      // keyboard activation never gets the reveal step
      if (event.detail === 0) return;
      if (tile.classList.contains(ACTIVE_CLASS)) return;

      event.preventDefault();
      this._closeAll();
      this._place(tile);
      tile.classList.add(ACTIVE_CLASS);
    }

    _closeAll() {
      this.tiles.forEach((tile) => tile.classList.remove(ACTIVE_CLASS));
    }

    _place(tile) {
      const card = tile.querySelector(CURSOR_CARD_SELECTOR);
      const grid = this.root.querySelector(GRID_SELECTOR);
      if (!card || !grid) return;

      const tileRect = tile.getBoundingClientRect();
      const gridRect = grid.getBoundingClientRect();
      const cardWidth = card.offsetWidth;

      // flush with whichever tile edge faces the outside of the grid, then pulled
      // back in when the card is wider than the room left on that side
      const alignEnd =
        tileRect.left - gridRect.left > gridRect.right - tileRect.right;
      let x = alignEnd ? tileRect.width - cardWidth : 0;
      const minX = gridRect.left - tileRect.left;
      const maxX = gridRect.right - tileRect.left - cardWidth;
      x = Math.min(Math.max(x, minX), maxX);

      card.style.setProperty("--cursor-card-x", `${Math.round(x)}px`);
      card.style.setProperty(
        "--cursor-card-y",
        `${Math.round(tileRect.height - TAP_OVERLAP)}px`
      );
    }

    _apply(tile, clientX, clientY) {
      const card = tile.querySelector(CURSOR_CARD_SELECTOR);
      if (!card) return;

      const cardWidth = card.offsetWidth;
      const cardHeight = card.offsetHeight;
      const viewportWidth = document.documentElement.clientWidth;
      const viewportHeight = document.documentElement.clientHeight;

      let x = clientX + OFFSET;
      let y = clientY + OFFSET;

      if (x + cardWidth > viewportWidth) x = clientX - OFFSET - cardWidth;
      if (y + cardHeight > viewportHeight) y = clientY - OFFSET - cardHeight;

      // a flip can overshoot the opposite edge when the card is wider than the
      // space left of the pointer
      x = Math.max(OFFSET, x);
      y = Math.max(OFFSET, y);

      card.style.setProperty("--cursor-card-x", `${Math.round(x)}px`);
      card.style.setProperty("--cursor-card-y", `${Math.round(y)}px`);
    }
  }

  function destroySection(root) {
    if (!root || !root._featuredCollectionCursor) return;
    root._featuredCollectionCursor.destroy();
    root._featuredCollectionCursor = null;
  }

  function initSection(section) {
    if (!section || !section.classList) return;
    if (!section.classList.contains(SECTION_CLASS)) return;

    const root = section.querySelector(ROOT_SELECTOR);
    if (!root) return;

    destroySection(root);
    root._featuredCollectionCursor = new FeaturedCollectionCursor(root);
  }

  if (document.currentScript && document.currentScript.parentElement) {
    initSection(document.currentScript.parentElement);
  }

  if (!window.__themeFeaturedCollectionEvents) {
    window.__themeFeaturedCollectionEvents = true;

    document.addEventListener("shopify:section:load", (event) => {
      initSection(event.target);
    });

    document.addEventListener("shopify:section:unload", (event) => {
      if (!event.target || !event.target.querySelectorAll) return;
      event.target.querySelectorAll(ROOT_SELECTOR).forEach(destroySection);
    });
  }
})();
